import { useCallback, useEffect, useState } from "react";

interface ServiceWorkerState {
  isSupported: boolean;
  isRegistered: boolean;
  isUpdateAvailable: boolean;
  isUpdating: boolean;
  showUpdateToast: boolean;
  registration: ServiceWorkerRegistration | null;
}

export function useServiceWorker() {
  const [state, setState] = useState<ServiceWorkerState>({
    isSupported: "serviceWorker" in navigator,
    isRegistered: false,
    isUpdateAvailable: false,
    isUpdating: false,
    showUpdateToast: false,
    registration: null,
  });

  // In der lokalen Entwicklung keinen Service Worker registrieren
  const isLocalLikeHost = (() => {
    try {
      const host = window.location.hostname;
      return (
        host.includes("localhost") ||
        host.includes("127.0.0.1") ||
        host.startsWith("192.168.") ||
        host.startsWith("172.30.") ||
        host === "::1"
      );
    } catch {
      return false;
    }
  })();

  // Service Worker nur in echter Produktion registrieren (nicht in Dev/Preview)
  // TEMPORÄR DEAKTIVIERT: Setze auf false, um Service Worker komplett zu deaktivieren
  const SW_DISABLED = localStorage.getItem("sw_disabled") === "true";

  const shouldRegisterSW =
    !SW_DISABLED && // TEMPORÄR: Service Worker deaktivieren
    state.isSupported &&
    window.location.protocol === "https:" &&
    !isLocalLikeHost &&
    !window.location.hostname.includes("vercel.app") && // Vercel Preview
    !window.location.hostname.includes("netlify.app"); // Netlify Preview

  const checkForUpdates = useCallback(
    async (registration?: ServiceWorkerRegistration) => {
      const reg = registration || state.registration;
      if (!reg) return;

      try {
        await reg.update();
        console.log("Service Worker Update-Check durchgeführt");
      } catch (error) {
        console.warn("Service Worker Update-Check fehlgeschlagen:", error);
      }
    },
    [state.registration]
  );

  // Automatische Update-Benachrichtigung (ohne User-Interaktion)
  const triggerUpdate = useCallback(
    (registration: ServiceWorkerRegistration) => {
      // Zeige Toast sofort
      setState(prev => ({
        ...prev,
        showUpdateToast: true,
        isUpdateAvailable: true,
      }));

      // Aktiviere Update automatisch (ohne Bestätigung)
      const waitingWorker = registration.waiting;
      if (waitingWorker) {
        waitingWorker.postMessage({ type: "SKIP_WAITING" });
        // Reload wird automatisch durch controllerchange ausgelöst
      }
    },
    []
  );

  const registerServiceWorker = useCallback(async () => {
    if (!shouldRegisterSW) {
      if (SW_DISABLED) {
        console.log(
          "🚫 Service Worker TEMPORÄR DEAKTIVIERT (sw_disabled=true)"
        );
      } else {
        console.log(
          "Service Worker deaktiviert (Dev/HTTP/localhost) – Registrierung wird übersprungen"
        );
      }
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
        // Cache-Busting: Verhindere, dass Browser alte SW-Version cached
        updateViaCache: "none",
      });

      console.log("Service Worker registriert:", registration);

      setState(prev => ({
        ...prev,
        isRegistered: true,
        registration,
      }));

      // Prüfe sofort auf wartende Updates
      // Next-Load Strategie: KEIN automatisches Update mehr!
      // Updates werden erst beim nächsten Reload aktiv (vermeidet Mischzustände)
      if (registration.waiting) {
        console.log(
          "Service Worker: Wartende Version gefunden - wird beim nächsten Reload aktiv"
        );
        // Zeige Toast, aber starte KEIN automatisches Update
        setState(prev => ({
          ...prev,
          showUpdateToast: true,
          isUpdateAvailable: true,
        }));
      }

      // Prüfe auf Updates
      checkForUpdates(registration);

      // Höre auf Update-Events
      registration.addEventListener("updatefound", () => {
        console.log("Service Worker Update gefunden");
        const newWorker = registration.installing;

        if (newWorker) {
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              console.log(
                "Neue Version installiert - wird beim nächsten Reload aktiv (Next-Load)"
              );
              // Next-Load Strategie: Zeige Toast, aber KEIN automatisches Update
              setState(prev => ({
                ...prev,
                showUpdateToast: true,
                isUpdateAvailable: true,
              }));
            }
          });
        }
      });

      // Höre auf Controller-Change (Update wurde aktiviert)
      // Next-Load Strategie: Nur reloaden bei expliziter User-Aktion
      // Automatische Updates werden beim nächsten Reload aktiv (vermeidet Mischzustände)
      let isExplicitUpdate = false;

      const handleControllerChange = () => {
        if (isExplicitUpdate) {
          console.log(
            "Service Worker Controller geändert - Seite wird neu geladen (explizites Update)"
          );
          setState(prev => ({
            ...prev,
            showUpdateToast: false,
            isUpdating: false,
          }));
          // Kurze Verzögerung, damit Toast sichtbar bleibt
          setTimeout(() => {
            window.location.reload();
          }, 500);
          isExplicitUpdate = false; // Reset
        } else {
          // Automatisches Update - nur loggen, nicht reloaden
          // Der neue SW wird beim nächsten Reload aktiv (Next-Load Strategie)
          console.log(
            "Service Worker Controller geändert - wird beim nächsten Reload aktiv (Next-Load)"
          );
        }
      };
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        handleControllerChange
      );

      // Speichere isExplicitUpdate Flag für updateServiceWorker
      (registration as any).__isExplicitUpdate = () => {
        isExplicitUpdate = true;
      };
    } catch (error) {
      console.error("Service Worker Registrierung fehlgeschlagen:", error);
    }
  }, [shouldRegisterSW, triggerUpdate, checkForUpdates]);

  const updateServiceWorker = useCallback(async () => {
    if (!state.registration || !state.isUpdateAvailable) return;

    setState(prev => ({ ...prev, isUpdating: true }));

    try {
      const waitingWorker = state.registration.waiting;
      if (waitingWorker) {
        // Markiere als explizites Update (für controllerchange Handler)
        if ((state.registration as any).__isExplicitUpdate) {
          (state.registration as any).__isExplicitUpdate();
        }
        // Sende Skip-Waiting Message an den Service Worker
        waitingWorker.postMessage({ type: "SKIP_WAITING" });
      }
    } catch (error) {
      console.error("Service Worker Update fehlgeschlagen:", error);
      setState(prev => ({ ...prev, isUpdating: false }));
    }
  }, [state.registration, state.isUpdateAvailable]);

  const dismissUpdate = useCallback(() => {
    setState(prev => ({
      ...prev,
      isUpdateAvailable: false,
    }));
  }, []);

  // Registriere Service Worker beim Mount
  useEffect(() => {
    if (shouldRegisterSW && !state.isRegistered) {
      registerServiceWorker();
    }
  }, [shouldRegisterSW, state.isRegistered, registerServiceWorker]);

  // controllerchange wird bereits in registerServiceWorker behandelt

  // Periodische Update-Checks (alle 30 Minuten)
  useEffect(() => {
    if (!state.isRegistered) return;

    const interval = setInterval(
      () => {
        checkForUpdates();
      },
      30 * 60 * 1000
    ); // 30 Minuten

    return () => clearInterval(interval);
  }, [state.isRegistered, checkForUpdates]);

  return {
    ...state,
    registerServiceWorker,
    checkForUpdates,
    updateServiceWorker,
    dismissUpdate,
  };
}
